import SelectFormField from '@components/Common/SelectFormField';
import FormField from '@components/Common/FormField';
import { useToast } from '@components/Common/Toast/ToastProvider';
import {
  getContractTemplateByIdQuery,
  getContractTemplatesQuery,
  sendPremiumContractMutation,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

interface Props {
  open: boolean;
  employerId: string;
  onClose: () => void;
  onSent: () => void;
}

interface TemplateSummary {
  _id: string;
  slug: string;
  title: string;
  status: string;
}

interface TemplateFull {
  _id: string;
  slug: string;
  title: string;
  bodyHtml: string;
  availableVariables: string[];
}

/**
 * Fills a contract template's placeholders and sends it to an employer.
 *
 * Company / contact / email / date are resolved server-side from the employer record; the admin
 * only fills the deal-specific values (tier, fee, term). A blank field keeps the server-resolved
 * value. E-signing is out of scope: this emails the contract for offline signing, then the admin
 * activates premium once it is returned.
 */
const SendContractDialog: React.FC<Props> = ({
  open,
  employerId,
  onClose,
  onSent,
}) => {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [full, setFull] = useState<TemplateFull | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedSlug('');
    setFull(null);
    setValues({});
    setError('');
    (async () => {
      try {
        const { data } = await fetchGraphQLApiData(getContractTemplatesQuery);
        const list: TemplateSummary[] = (data?.getContractTemplates || []).filter(
          (t: TemplateSummary) => t.status === 'ACTIVE',
        );
        setTemplates(list);
      } catch {
        setError('Could not load contract templates.');
      }
    })();
  }, [open]);

  const handleSelect = async (slug: string) => {
    setSelectedSlug(slug);
    setFull(null);
    setValues({});
    const summary = templates.find((t) => t.slug === slug);
    if (!summary) return;
    setLoading(true);
    try {
      const { data } = await fetchGraphQLApiData(
        getContractTemplateByIdQuery(summary._id),
      );
      const t: TemplateFull = data?.getContractTemplateById;
      setFull(t);
      const init: Record<string, string> = {};
      (t?.availableVariables || []).forEach((v) => {
        init[v] = '';
      });
      setValues(init);
    } catch {
      setError('Could not load the template.');
    } finally {
      setLoading(false);
    }
  };

  const preview = (): string => {
    if (!full) return '';
    let html = full.bodyHtml || '';
    Object.entries(values).forEach(([k, v]) => {
      html = html.split(`{{${k}}}`).join(v || `{{${k}}}`);
    });
    return html;
  };

  const handleSend = async () => {
    if (!full || !employerId) return;
    setSending(true);
    setError('');
    try {
      const filled = Object.entries(values)
        .filter(([, v]) => v && v.trim())
        .map(([key, value]) => ({ key, value }));
      await fetchGraphQLApiData(
        sendPremiumContractMutation(employerId, full.slug, filled),
        { showStatus: true, message: 'Sending contract' },
      );
      showToast('Contract sent to the employer', 'success');
      onSent();
    } catch (e: unknown) {
      setError(
        (e as { message?: string })?.message || 'Could not send the contract.',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send Premium Hiring contract</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography
          variant="body2"
          sx={{ color: 'var(--color-text-muted)', mb: 2 }}
        >
          Pick a template and fill the deal-specific values. Company, contact,
          email and date are filled automatically; leave a field blank to use the
          auto value.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <SelectFormField
            required
            name="template"
            label="Contract template"
            size="small"
            value={selectedSlug}
            options={
              templates.length === 0
                /* There is no longer a screen that creates one. The Contracts page managed these
                   and has been replaced by Agreements, which holds signed documents rather than
                   the templates that produce them, so this now says who to ask rather than where
                   to click. */
                ? [{ value: '', label: 'No active templates on file. Ask engineering to add one.', disabled: true }]
                : templates.map((t) => ({ value: t.slug, label: t.title }))
            }
            onChange={(_n, v) => handleSelect(String(v))}
          />
        </Box>

        {loading && <Typography variant="body2">Loading template...</Typography>}

        {full && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 240px' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Values
              </Typography>
              {(full.availableVariables || []).length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: 'var(--color-text-muted)' }}
                >
                  This template has no placeholders.
                </Typography>
              )}
              {(full.availableVariables || []).map((v) => (
                <Box key={v} sx={{ mb: 1 }}>
                  <FormField
                    required={false}
                    name={v}
                    label={v}
                    value={values[v] ?? ''}
                    onChange={(_n, next) =>
                      setValues((prev) => ({ ...prev, [v]: String(next) }))
                    }
                  />
                </Box>
              ))}
            </Box>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 'var(--border-radius-sm)',
                  maxHeight: 320,
                  overflow: 'auto',
                  // The contract preview renders admin-authored HTML written
                  // for a printed page, so it keeps a paper fill on purpose.
                  bgcolor: 'var(--paper)',
                }}
                // Admin-authored template content, trusted.
                dangerouslySetInnerHTML={{ __html: preview() }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSend} disabled={!full || sending}>
          {sending ? 'Sending...' : 'Send contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendContractDialog;
